// Command-source registry — lets a live surface feed context-derived commands
// into the one root ⌘K palette. Plugin `commands` are declarative (resolved from
// the host ctx); but a surface like the process editor derives its commands from
// live, in-render state (the step-edit api + active step) that no static
// descriptor can capture. So such a surface REGISTERS a source function while
// mounted, and the CommandPaletteHost collects every source at palette-open.
//
// Collection is pull-based (only at open), so there is no re-render churn: the
// provider keeps a mutable set of source fns behind a ref; register/unregister
// mutate it; the host reads the current set when it opens.

import { createContext, useContext, useEffect, useRef, type ReactNode } from "react";
import { createElement } from "react";
import type { WorkbenchHostContext } from "./hostContext.js";
import type { PaletteCommand } from "./CommandPalette.js";

/** A source of commands derived from a surface's live state. Receives the host
 *  ctx (a source may gate on it, or ignore it and close over its own api). */
export type CommandSource = (ctx: WorkbenchHostContext) => readonly PaletteCommand[];

export interface CommandSourceRegistry {
  /** Register a source; returns an unregister fn (call on unmount). */
  add(source: CommandSource): () => void;
  /** Collect every registered source's commands, in registration order. */
  collect(ctx: WorkbenchHostContext): readonly PaletteCommand[];
}

export function createCommandSourceRegistry(): CommandSourceRegistry {
  const sources = new Set<CommandSource>();
  return {
    add(source) {
      sources.add(source);
      return () => sources.delete(source);
    },
    collect(ctx) {
      const out: PaletteCommand[] = [];
      for (const s of sources) out.push(...s(ctx));
      return out;
    },
  };
}

const NOOP_REGISTRY: CommandSourceRegistry = { add: () => () => {}, collect: () => [] };

const Ctx = createContext<CommandSourceRegistry>(NOOP_REGISTRY);

/** The command-source registry. Defaults to a NOOP registry, so a surface
 *  registering outside a provider silently no-ops (viewer/SSR safe). */
export function useCommandSourceRegistry(): CommandSourceRegistry {
  return useContext(Ctx);
}

export function CommandSourceProvider({ children }: { readonly children: ReactNode }): ReactNode {
  // Lazy-init so the registry (a Set) is built once, not per render.
  const ref = useRef<CommandSourceRegistry | null>(null);
  if (ref.current === null) ref.current = createCommandSourceRegistry();
  return createElement(Ctx.Provider, { value: ref.current }, children);
}

/** Register `source` for the lifetime of the calling component. The registered
 *  wrapper is stable (registered once); it delegates to the latest `source` via a
 *  ref, so a source closing over changing live state stays fresh without
 *  re-registering. */
export function useRegisterCommandSource(source: CommandSource): void {
  const registry = useCommandSourceRegistry();
  const ref = useRef(source);
  ref.current = source;
  useEffect(() => registry.add((ctx) => ref.current(ctx)), [registry]);
}
